import {
    Component,
    Injectable,
    inject
} from '@angular/core';

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";

import { lastValueFrom } from 'rxjs';

import {
    BaseDialogService,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SalesToBeInvoiced } from './models/sales_to_be_invoiced.model';
import { TranslateModule } from '@ngx-translate/core';
import { restManager } from 'app/ajs-upgraded-providers';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { MatSelectModule } from '@angular/material/select';

export type TilbyDialogData = { sale?: SalesToBeInvoiced };

type TilbyDialogOutput = {
    action: 'summary' | 'details',
    toBeInvoiced: SalesToBeInvoiced
};

@Component({
    selector: 'app-tilby-dialog',
    templateUrl: './history-summary-invoice-viewing-ui-dialog.component.html',
    styleUrls: ['./history-summary-invoice-viewing-ui-dialog.component.scss'],
    imports: [
        CommonModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatTableModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        TilbyCurrencyPipe,
        TranslateModule,
        NgxSkeletonLoaderModule,
        MatProgressSpinnerModule,
    ],
    standalone: true,
    providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }]
})
export class HistorySummaryInvoiceViewingUiDialogComponent {
    protected readonly data: TilbyDialogData = inject(MAT_DIALOG_DATA);
    private readonly restManagerService = inject(restManager);
    private readonly matDialogRef = inject(MatDialogRef);

    private dataSource: SalesToBeInvoiced[] = [];
    protected filteredDataSource: SalesToBeInvoiced[] = [];
    protected isLoading = false;

    protected displayedColumns = [
        'name',
        'invoice_type',
        'documents_to_be_invoiced',
        'details',
        'total_to_be_invoiced',
        'star',
    ];

    range = new FormGroup({
        closed_at_since: new FormControl<Date>(this.fromDateToUTC(new Date(new Date().getFullYear(), 0, 1)), { validators: [Validators.required] }),
        closed_at_max: new FormControl<Date>(new Date(), { validators: [Validators.required] }),
    });

    selectedType: string = "collected";

    constructor(
    ) {
        if (this.data.sale) {
            this.filteredDataSource.push(this.data.sale!);
            this.dataSource = this.filteredDataSource;
        } else {
            this.fetchToBeInvoiced();
        }
    }

    ngAfterViewInit() {
        const title = document.getElementsByClassName('tw-flex tw-flex-row tw-flex-1')[0];

        if (title) {
            title.classList.add('tw-items-center');
        }
    }

    async fetchToBeInvoiced() {
        this.isLoading = true;

        try {
            const dates = this.getISOStringStartEndDates();
            const data = await this.restManagerService.getList("analytics/sales/to_be_invoiced", { closed_at_max: dates.closed_at_max, closed_at_since: dates.closed_at_since });

            this.dataSource = this.filteredDataSource = data.results;
            this.changeType({ value: this.selectedType });
        } finally {
            this.isLoading = false;
        }
    }

    addEvent() {
        if (this.range.value.closed_at_since?.getFullYear()! < this.range.value.closed_at_max?.getFullYear()!) {
            this.range.controls.closed_at_max.setErrors({ matEndYearInvalid: true });
        }

        if (!this.range.valid) {
            return;
        }

        this.fetchToBeInvoiced();
    }

    onClose() {
        !this.getISOStringStartEndDates().closed_at_since && this.range.controls.closed_at_since.setErrors({ matStartDateInvalid: true })
        !this.getISOStringStartEndDates().closed_at_max && this.range.controls.closed_at_max.setErrors({ matEndDateInvalid: true })
    }

    getISOStringStartEndDates() {
        return {
            closed_at_since: this.range.value.closed_at_since && this.generateISOString(this.range.value.closed_at_since!),
            closed_at_max: this.range.value.closed_at_max && new Date(new Date(this.range.value.closed_at_max!).setHours(24, 59, 59)).toISOString()
        }
    }

    generateISOString(insertDate: Date) {
        return new Date(Date.UTC(insertDate.getFullYear(), insertDate.getMonth(), insertDate.getDate())).toISOString().split('T')[0] + 'T00:00:00.000Z';
    }

    fromDateToUTC(date: Date) {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
    }

    changeType(invoice_type: any) {
        this.filteredDataSource = invoice_type.value
            ? this.dataSource.filter((sale) => sale.collected === (invoice_type.value === "collected"))
            : this.dataSource;
    }

    protected confirm(res: SalesToBeInvoiced) {
        this.matDialogRef.close({
            action: 'summary',
            toBeInvoiced: res
        });
    }

    protected showDetails(res: SalesToBeInvoiced) {
        this.matDialogRef.close({
            action: 'details',
            toBeInvoiced: res
        });
    }
}

@Injectable({
    providedIn: 'root'
})
export class HistorySummaryInvoiceViewingUiService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(salesToBeInvoicedCustomer?: SalesToBeInvoiced): Promise<TilbyDialogOutput | undefined> {
        return lastValueFrom(this.dialogRef.open(HistorySummaryInvoiceViewingUiDialogComponent, {
            ...this.switchMobileDesktopDimensions({ width: '800px' }),
            disableClose: true,
            data: { sale: salesToBeInvoicedCustomer }
        }).afterClosed());
    }
}
